import { array } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';

import type { DSSetup, PopupListItem, Service } from 'webapp-types';
import type { BlockVisualization } from 'webapp/enums/enums';
import { BlockRenderStep, PopupEvent } from 'webapp/enums/enums';
import { AppEvent } from 'webapp/enums/events';
import { TrackingEvent } from 'webapp/enums/tracking';
import { isAlive } from 'webapp/utils/common';
import { getRelativeTime } from 'webapp/utils/number';

import definite from 'webapp/helpers/definite';
import joinClasses from 'webapp/helpers/join-classes';
import didInsert from 'webapp/modifiers/did-insert';
import willDestroy from 'webapp/modifiers/will-destroy';
import BlockContentAiRegenerateIconComponent from 'webapp/components/block/content/ai-regenerate-icon';
import BlockContentVisualizationSwitchComponent from 'webapp/components/block/content/visualization-switch';
import BlockMasterFilterComponent from 'webapp/components/block/master-filter';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import DataPerformanceSummaryButtonComponent from 'webapp/components/data-performance/summary-button';
import FormRangeInlineComponent, { AttachToOption } from 'webapp/components/form/range/inline';
import LoaderSvgComponent from 'webapp/components/loader-svg';

import type { BoardPreviewRecord } from 'webapp/models';
import type BlockRenderer from 'webapp/models-custom/block-renderer';
import type { ChangeVisualizationWithPossibleConfirmFnReturnType } from 'webapp/models-custom/block-setup';
import type BlockState from 'webapp/models-custom/block-state';
import type { DataPerformanceSummaryObject } from 'webapp/models-custom/data-performance-summary/block';
import type DateRange from 'webapp/models-custom/date-range';

type RegenerateOrigin =
	| 'designer'
	| 'editor'
	| 'reports_preview'
	| 'reports_public_URL'
	| 'databoards_preview'
	| 'databoards_public_URL'
	| '';

interface Args {
	blockState: BlockState;
	blockRenderer: BlockRenderer;
	canChangeDateRange: boolean;
	canChangeFilter: boolean;
	canChangeVisualization: boolean;
	onlyCompatibleVisualizations: boolean;
	dataPerformanceSummaryObject: DataPerformanceSummaryObject | null;
	changeVisualization: (
		to: BlockVisualization,
	) => ChangeVisualizationWithPossibleConfirmFnReturnType;
	changeDimensionsFilter: (
		dimensions?: string[] | null,
		filters?: DSSetup.MetricSetupFiltersData | null,
	) => void;
	skipMasterFilter?: boolean;
	canEdit: boolean;
}

type Action = 'delete';
type Option = PopupListItem<Action>;

export default class BlockContentTopLineComponent extends Component<Args> {
	@service('account') declare accountService: Service.Account;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('model-manager/blocks') declare mmBlocksService: Service.ModelManager.Blocks;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('popup') declare popupService: Service.Popup;
	@service('request') declare requestService: Service.Request;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('model-manager/reports') declare mmReportsService: Service.ModelManager.Reports;

	@tracked moreContextOpened = false;
	@tracked dataPerformanceSummaryOpened = false;
	@tracked visualizationSwitchOpened = false;
	@tracked masterFilterOpened = false;

	get block() {
		return this.args.blockState?.block;
	}

	get blockSetup() {
		return this.block.blockSetup;
	}

	get visualization() {
		return this.blockSetup.visualization;
	}

	get isBlockOptionsActive() {
		return (
			this.dataPerformanceSummaryOpened ||
			this.moreContextOpened ||
			this.visualizationSwitchOpened ||
			this.masterFilterOpened
		);
	}

	get trackingOrigin() {
		return this.block.isFromReport ? 'datablock_editor' : 'datablock_designer';
	}

	get metricLimitations() {
		return this.args.blockState?.limitationsCM; // through block-state, to catch changes
	}

	get dateText(): string {
		const dateText = this.blockSetup.firstShownDataSelect?.dateText;
		if (dateText) {
			return `(${dateText})`;
		}
		return '';
	}

	get isRangeSelectDisabled(): boolean {
		return !this.args.canChangeDateRange;
	}

	get hasGranulation(): boolean {
		return this.block.blockSetup.visualization.hasGranulation;
	}

	get showDataLoader(): boolean {
		if (this.mmBlocksService.areSamplesForBlockLoading(this.block.id)) {
			return true;
		}

		let showLoader = false;

		const hasBlockWideDataLoader = this.blockSetup.hasBlockWideDataLoader;
		if (hasBlockWideDataLoader) {
			let syncStatusObjects = this.block.statusObjects.filter((item) => item.isSync);

			if (this.blockSetup.isBlockWideDataLoaderOnlyFromSeries) {
				// for charts with number, show loader for series here
				syncStatusObjects = syncStatusObjects.filter(
					(item) => item.dataLocation === 'series',
				);
			}

			showLoader = syncStatusObjects.length > 0;
		}

		return showLoader;
	}

	@cached
	get showBlockPreviewButtonOnBoard() {
		let board = this.block.boardObj;
		const isBoardOrLoopPopupPreviewOpen = this.mmBoardsService.isBoardPreviewPopupOpen(),
			route = this.navigationService.activatedRoute;

		if (!board) {
			const boardId = (this.block?.board as BoardPreviewRecord)?.boardId;

			if (boardId) {
				board = this.mmBoardsService.findModel(String(boardId));
			}
		}

		return (
			(isBoardOrLoopPopupPreviewOpen ||
				route.startsWith('boards.tabs') ||
				route.startsWith('boards.view') ||
				route.startsWith('boards.loops.loop')) &&
			!!board?.canCurrentUserEdit &&
			!this.block.isFeatureLimited &&
			!this.accountService.isDemoAccount
		);
	}

	@cached
	get showBlockPreviewButtonOnReport() {
		const report = this.block.reportSlideFragment?.report;
		const isReportPopupPreviewOpen = this.mmReportsService.isReportPreviewPopupOpen(),
			route = this.navigationService.activatedRoute;

		return (
			(isReportPopupPreviewOpen || route.startsWith('reports.view')) &&
			!!report?.canCurrentUserEdit &&
			!this.block.isFeatureLimited &&
			!this.accountService.isDemoAccount
		);
	}

	@cached
	get showBlockPreviewButton() {
		return this.showBlockPreviewButtonOnBoard || this.showBlockPreviewButtonOnReport;
	}

	get showDataPerformanceSummaryButton() {
		return (
			this.args.dataPerformanceSummaryObject &&
			!this.showBlockPreviewButton &&
			!this.block.isAiSummary &&
			!this.block.isFromReport &&
			this.block.hasAnyNumberDataSelect
		);
	}

	get showMasterFilter() {
		return (
			this.args.canChangeFilter && !this.args.skipMasterFilter && !this.showBlockPreviewButton
		);
	}

	get showVisualizationSwitch() {
		return (
			this.args.canChangeVisualization &&
			!this.showBlockPreviewButton &&
			!this.block.aiSummaryIsEdited &&
			!(this.isBlockFromReport && !this.args.canEdit)
		);
	}

	get showAiActions() {
		return (
			this.visualization.isAiSummary &&
			!this.block.showLimitationsOverlay &&
			(!this.block.isOnTemplate || this.navigationService.isDesigner)
		);
	}

	get showEditAiSummaryButton() {
		return (
			this.showAiActions &&
			// canEdit does not work for databoards because it has to be active also (does not show on hover unless clicked)
			((this.args.canEdit && this.isBlockFromReport) || this.navigationService.isDesigner)
		);
	}

	get optionsForMoreContext() {
		const options: Option[] = [];

		const canDelete = this.block.isFromReportBlockFragment;
		if (canDelete) {
			// deleting slide fragment
			options.push({
				class: 'delete',
				text: 'Delete',
				action: 'delete',
			});
		}

		return options;
	}

	get showMoreContext() {
		return false; // for now
	}

	get isBlockFromReport() {
		return this.block.isFromReport;
	}

	get lastSync() {
		return this.args.blockState.block.lastSync;
	}

	get showAiSummaryEdited() {
		return this.block.aiSummaryIsEdited;
	}

	get relativeLastEditedTimeText() {
		if (!this.block.aiSummaryEditedOn) {
			return '';
		}

		return getRelativeTime(this.block.aiSummaryEditedOn);
	}

	get lastEditedBy() {
		if (!this.block.aiSummaryEditedBy) {
			return '';
		}

		return this.block.aiSummaryEditedBy;
	}

	get showDeleteFragmentButton() {
		return this.isBlockFromReport && this.args.canEdit;
	}

	get regenerateOrigin(): RegenerateOrigin {
		if (!this.isBlockFromReport) {
			// databoards blocks
			if (this.navigationService.isDesigner) {
				return 'designer';
			} else if (this.navigationService.isMainApp) {
				return 'databoards_preview';
			} else {
				return 'databoards_public_URL';
			}
		} else {
			// reports blocks
			if (this.navigationService.isReportEditorSubpage) {
				return 'editor';
			} else if (this.navigationService.isMainApp) {
				return 'reports_preview';
			} else {
				return 'reports_public_URL';
			}
		}
	}

	get dateRangeAttachTo(): AttachToOption {
		return AttachToOption.TEXT;
	}

	@action
	openMoreContext(e: PointerEvent) {
		if (this.accountService.handleDemo()) {
			return;
		}

		if (this.moreContextOpened) {
			return;
		}

		const attachTo = e.target as HTMLElement;
		const popup = this.popupService.createListPopup(attachTo);

		if (popup) {
			popup.open({
				itemsList: this.optionsForMoreContext,
				doAction: (actionName: string) => {
					this.doOptionsAction(actionName);
				},
			});

			this.moreContextOpened = true;
			popup.on(PopupEvent.CLOSED, this, () => {
				if (isAlive(this)) {
					this.moreContextOpened = false;
				}
			});
		}
	}

	@action
	updateDataPerformanceSummaryActive(active: boolean) {
		this.dataPerformanceSummaryOpened = active;
	}

	@action
	updateMasterFilterActive(active: boolean) {
		this.masterFilterOpened = active;
	}

	@action
	updateVisualizationSwitchActive(active: boolean) {
		this.visualizationSwitchOpened = active;
	}

	@action
	doOptionsAction(actionName: string): void {
		this[actionName as Action]();
	}

	// used on action from more context
	@action
	delete() {
		const fragment = this.block.reportSlideFragment;
		if (!fragment) {
			return;
		}
		// must forward to fragment-grid to take care of resizes etc
		this.eventManagerService.trigger(AppEvent.REPORT_FRAGMENT_DELETE, { fragment });
	}

	// used on direct delete button click
	@action
	onDelete(event: MouseEvent) {
		const fragment = this.block.reportSlideFragment;
		if (!fragment) {
			return;
		}

		event.stopPropagation();
		// must forward to fragment-grid to take care of resizes etc
		this.eventManagerService.trigger(AppEvent.REPORT_FRAGMENT_DELETE, { fragment });
	}

	@action
	dateRangeChanged(dateRangeCM: DateRange /*, compareCM: MetricLimitationCompare*/): void {
		const metricLimitations = this.metricLimitations;

		if (!metricLimitations) {
			return;
		}

		const hasGranulation = this.hasGranulation,
			currentFormula = metricLimitations.dateRange?.formula;

		if (hasGranulation) {
			metricLimitations.updateDateRangeFromFormula(dateRangeCM.formula);
			// metricLimitations.compare.updateFromFormula(compareCM.getFormula());
		} else {
			// update only range - because we were setting only range
			metricLimitations.updateDateRangeFromOnlyRangeFormula(dateRangeCM.rangeFormula);
		}

		// only send tracking event if we did not have granulation
		// or the range part is actually changed
		// the granulation change event is tracked in the popup itself
		if (!hasGranulation || !currentFormula || !dateRangeCM.isRangeEqual(currentFormula)) {
			this.analyticsService.event(TrackingEvent.DATE_RANGE_CHANGED, {
				origin: this.trackingOrigin,
			});
		}
	}

	@action
	editAiSummary(event: PointerEvent) {
		event.stopPropagation();

		this.eventManagerService.trigger(AppEvent.AI_SUMMARY_EDITING_STATE_CHANGED, {
			blockId: this.block.id,
			isEditing: true,
		});
	}

	@action
	async openPopupBlockPreview(e: PointerEvent) {
		const elementToAnimateFrom: HTMLElement = (e.target as HTMLElement).closest(
			'.block-content',
		)!;

		let model = this.block,
			boardState = null;
		if (model.isPreview) {
			const originalBlockId = String(model.originateFrom);
			let originalBlock = this.mmBlocksService.findModel(originalBlockId)!;

			if (!originalBlock) {
				await this.mmBlocksService.requestSingleBlock(originalBlockId);
				originalBlock = this.mmBlocksService.findModel(originalBlockId)!;
			}

			model = originalBlock;
			// we need board state in case we are editing original block of block preview
			// so we can reset board preview to orignal board state after saving cloned block
			boardState = this.args.blockState.boardState;
		}

		this.mmBlocksService.openPreviewPopup(
			{
				model,
				boardState,
				trackingOrigin: 'databoard',
			},
			elementToAnimateFrom,
		);
	}

	@action
	setupView(): void {
		const blockRenderer = this.args.blockRenderer;
		blockRenderer.registerStepHandler(BlockRenderStep.TOP, this, () => {
			return Promise.resolve();
		});

		// prepare functions for teardown, since they dont have access to this.args
		this.teardownFns.push(() => {
			blockRenderer.unregisterStepHandler(BlockRenderStep.TOP, this);
		});
	}

	private teardownFns: (() => void)[] = [];

	@action
	teardownView(): void {
		this.teardownFns.forEach((fn) => fn());
	}
	<template>
		<div
			class={{joinClasses
				(array "block-content__top" (if this.block.name "block-content__top--has-title"))
			}}
			{{didInsert this.setupView}}
			{{willDestroy this.teardownView}}
		>

			{{#if this.showAiSummaryEdited}}
				<div class="ai-summary-last-edited">
					<CommonUiIconComponent @name="Clock" @size="sm" />
					<div class="ai-summary-edited">
						<span>
							Last edited
							{{this.relativeLastEditedTimeText}}
							by
							{{this.block.aiSummaryEditedBy}}
						</span>
					</div>
				</div>
			{{else}}
				<FormRangeInlineComponent
					@metricLimitations={{definite this.metricLimitations}}
					@dateText={{this.dateText}}
					@isDisabled={{this.isRangeSelectDisabled}}
					@openAllBlockPreviewProps={{this.hasGranulation}}
					@onChange={{this.dateRangeChanged}}
					@trackingOrigin={{this.trackingOrigin}}
					@labelText=""
					@addEm={{true}}
					@theme="dark"
					@attachTo={{this.dateRangeAttachTo}}
				/>
			{{/if}}

			<div class="block-options {{if this.isBlockOptionsActive 'active'}}">
				{{#if this.showBlockPreviewButton}}
					<div
						role="button"
						class="metric-detail-button"
						{{on "click" this.openPopupBlockPreview}}
					>
						<CommonUiIconComponent @name="Expand" @size="dynamic" />
					</div>
				{{/if}}
				{{#if this.showAiActions}}
					<BlockContentAiRegenerateIconComponent
						@blockState={{@blockState}}
						@dataPerformanceSummaryObject={{@dataPerformanceSummaryObject}}
						@regenerateOrigin={{this.regenerateOrigin}}
					/>
					{{#if this.showEditAiSummaryButton}}
						<a role="button" class="ai-edit" {{on "click" this.editAiSummary}}>
							<CommonUiIconComponent @name="Pencil" @size="dynamic" />
						</a>
					{{/if}}
				{{/if}}
				{{#if this.showDataPerformanceSummaryButton}}
					<DataPerformanceSummaryButtonComponent
						@object={{@dataPerformanceSummaryObject}}
						@isDataIncomplete={{@blockState.isDataIncomplete}}
						@onPopupOpenStateChange={{this.updateDataPerformanceSummaryActive}}
					/>
				{{/if}}
				{{#if this.showMasterFilter}}
					<BlockMasterFilterComponent
						@blockState={{@blockState}}
						@save={{@changeDimensionsFilter}}
						@updateIsActive={{this.updateMasterFilterActive}}
						@theme="dark"
						@iconSize="dynamic"
					/>
				{{/if}}
				{{#if this.showVisualizationSwitch}}
					<BlockContentVisualizationSwitchComponent
						@blockState={{@blockState}}
						@onlyCompatible={{@onlyCompatibleVisualizations}}
						@changeVisualization={{@changeVisualization}}
						@updateIsActive={{this.updateVisualizationSwitchActive}}
						@theme="dark"
					/>
				{{/if}}
				{{#if this.showMoreContext}}
					<div
						role="button"
						class="context {{if this.moreContextOpened 'active'}}"
						{{on "click" this.openMoreContext}}
					>
						<CommonUiIconComponent @name="Ellipsis" @size="dynamic" />
					</div>
				{{/if}}
				{{#if this.showDataLoader}}
					<div class="status-loader">
						<LoaderSvgComponent />
					</div>
				{{/if}}
				{{#if this.showDeleteFragmentButton}}
					<a role="button" class="delete-fragment-button" {{on "click" this.onDelete}}>
						<CommonUiIconComponent @name="Trash" @size="dynamic" />
					</a>
				{{/if}}
			</div>

		</div>
	</template>
}
